import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Button } from './Button';
import { Input } from './Input';
import { Plus, Search, ChevronDown, X } from 'lucide-react';
import clsx from 'clsx';
import {
  venueTypes,
  statusOptions,
  musicStyles,
  MONTHS,
  emptyMonthPeriod,
} from './venueConstants';
import type { AdminCoreVenue, AdminCoreVenueUpdate, Address, MonthPeriod, Month } from '../../types';
import { cleanAddress, emptyAddress } from '../../utils/addressFormat';

const EDITABLE_FIELDS: Record<string, string> = {
  type: 'Type',
  styles: 'Styles',
  name: 'Name & Status',
  address: 'Address',
  concert_period: 'Concert Period',
};

interface CoreVenueFieldEditorProps {
  venue: AdminCoreVenue;
  field: string;
  position: { x: number; y: number };
  onSave: (venueId: string, data: AdminCoreVenueUpdate) => Promise<void>;
  onClose: () => void;
}

export function CoreVenueFieldEditor({
  venue,
  field,
  position,
  onSave,
  onClose,
}: CoreVenueFieldEditorProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  const [name, setName] = useState(venue.name || '');
  const [type, setType] = useState(venue.type);
  const [status, setStatus] = useState(venue.status || 'open');
  const [styles, setStyles] = useState<string[]>(venue.styles || []);
  const [newStyle, setNewStyle] = useState('');
  const [address, setAddress] = useState<Address>(venue.address || emptyAddress());
  const [concertPeriod, setConcertPeriod] = useState<MonthPeriod>(venue.concert_period || emptyMonthPeriod());

  const [typeDropdownOpen, setTypeDropdownOpen] = useState(false);
  const [typeSearch, setTypeSearch] = useState('');
  const typeDropdownRef = useRef<HTMLDivElement>(null);

  const [adjustedPos, setAdjustedPos] = useState(position);

  useEffect(() => {
    if (panelRef.current) {
      const rect = panelRef.current.getBoundingClientRect();
      let { x, y } = position;
      if (x + rect.width > window.innerWidth - 16) x = window.innerWidth - rect.width - 16;
      if (y + rect.height > window.innerHeight - 16) y = window.innerHeight - rect.height - 16;
      if (x < 16) x = 16;
      if (y < 16) y = 16;
      setAdjustedPos({ x, y });
    }
  }, [position]);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) onClose();
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (typeDropdownRef.current && !typeDropdownRef.current.contains(e.target as Node)) {
        setTypeDropdownOpen(false);
        setTypeSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fieldLabel = EDITABLE_FIELDS[field];
  if (!fieldLabel) return null;

  const handleSave = async () => {
    setError('');
    try {
      setIsSaving(true);
      let data: AdminCoreVenueUpdate = {};

      switch (field) {
        case 'name':
          if (!name.trim()) { setError('Name is required'); return; }
          data = { name: name.trim(), status };
          break;
        case 'type':
          data = { type };
          break;
        case 'styles':
          data = { styles };
          break;
        case 'address':
          data = { address: cleanAddress(address) };
          break;
        case 'concert_period':
          data = { concert_period: concertPeriod };
          break;
      }

      await onSave(venue.id, data);
      onClose();
    } catch (err: any) { // eslint-disable-line @typescript-eslint/no-explicit-any
      setError(err.response?.data?.detail || 'Failed to update');
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddStyle = (style: string) => {
    if (style && !styles.includes(style)) {
      setStyles([...styles, style]);
      setNewStyle('');
    }
  };

  const handleRemoveStyle = (style: string) => {
    setStyles(styles.filter(s => s !== style));
  };

  const updateAddressField = (addressField: keyof Address, value: string) => {
    setAddress(prev => ({ ...prev, [addressField]: value || undefined }));
  };

  const toggleMonth = (period: MonthPeriod, setPeriod: (p: MonthPeriod) => void, month: Month) => {
    const months = period.months.includes(month)
      ? period.months.filter(m => m !== month)
      : [...period.months, month];
    setPeriod({ months, all_year: false });
  };

  const toggleAllYear = (period: MonthPeriod, setPeriod: (p: MonthPeriod) => void) => {
    setPeriod({ all_year: !period.all_year, months: [] });
  };

  const renderMonthPeriodEditor = (period: MonthPeriod, setPeriod: (p: MonthPeriod) => void) => (
    <div className="flex flex-wrap gap-1">
      <button
        type="button"
        onClick={() => toggleAllYear(period, setPeriod)}
        className={clsx(
          'px-2 py-0.5 rounded-sm text-xs font-medium border',
          period.all_year
            ? 'border-primary-600 bg-primary-900/30 text-primary-400'
            : 'border-dark-border bg-dark-elevated text-dark-text-secondary hover:bg-dark-border'
        )}
      >
        All
      </button>
      {MONTHS.map(({ value, short }) => (
        <button
          key={value}
          type="button"
          onClick={() => toggleMonth(period, setPeriod, value)}
          disabled={period.all_year}
          className={clsx(
            'px-1.5 py-0.5 rounded-sm text-xs font-medium border',
            period.all_year
              ? 'border-dark-border bg-dark-bg text-dark-text-muted cursor-not-allowed'
              : period.months.includes(value)
              ? 'border-primary-600 bg-primary-900/30 text-primary-400'
              : 'border-dark-border bg-dark-elevated text-dark-text-secondary hover:bg-dark-border'
          )}
        >
          {short}
        </button>
      ))}
    </div>
  );

  const renderFieldEditor = () => {
    switch (field) {
      case 'name':
        return (
          <div className="space-y-3">
            <Input label="Venue Name" type="text" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
            <div>
              <label className="block text-xs font-medium text-dark-text-secondary mb-1">Status</label>
              <select value={status} onChange={(e) => setStatus(e.target.value as AdminCoreVenue['status'])} className="input text-sm">
                {statusOptions.map(({ value, label }) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
          </div>
        );

      case 'type':
        return (
          <div ref={typeDropdownRef} className="relative">
            <label className="block text-xs font-medium text-dark-text-secondary mb-1">Type</label>
            <button
              type="button"
              onClick={() => { setTypeDropdownOpen(!typeDropdownOpen); setTypeSearch(''); }}
              className="w-full flex items-center justify-between px-3 py-2 bg-dark-elevated border border-dark-border rounded-lg text-sm font-medium text-dark-text-primary hover:bg-dark-border focus:outline-hidden focus:ring-2 focus:ring-primary-500"
            >
              <span>{venueTypes.find(t => t.value === type)?.label || 'Select type'}</span>
              <ChevronDown className={clsx('w-4 h-4 ml-2 text-dark-text-secondary transition-transform', typeDropdownOpen && 'rotate-180')} />
            </button>
            {typeDropdownOpen && (
              <div className="absolute z-50 mt-1 w-full bg-dark-elevated border border-dark-border rounded-lg shadow-lg overflow-hidden">
                <div className="flex items-center gap-2 px-3 py-1.5 border-b border-dark-border">
                  <Search className="w-3.5 h-3.5 text-dark-text-muted" />
                  <input
                    type="text"
                    placeholder="Search..."
                    value={typeSearch}
                    onChange={(e) => setTypeSearch(e.target.value)}
                    className="w-full bg-transparent text-sm text-dark-text-primary placeholder:text-dark-text-muted focus:outline-hidden"
                    autoFocus
                  />
                </div>
                <div className="max-h-48 overflow-y-auto">
                  {venueTypes
                    .filter(({ label }) => label.toLowerCase().includes(typeSearch.toLowerCase()))
                    .map(({ value, label }) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => { setType(value); setTypeDropdownOpen(false); setTypeSearch(''); }}
                        className={clsx(
                          'w-full text-left px-3 py-2 text-sm',
                          type === value
                            ? 'bg-primary-900/30 text-primary-400'
                            : 'text-dark-text-secondary hover:bg-dark-border'
                        )}
                      >
                        {label}
                      </button>
                    ))}
                </div>
              </div>
            )}
          </div>
        );

      case 'styles':
        return (
          <div>
            <label className="block text-xs font-medium text-dark-text-secondary mb-2">Music Styles</label>
            <div className="flex gap-2 mb-2">
              <Input type="text" placeholder="Custom style..." value={newStyle} onChange={(e) => setNewStyle(e.target.value)} />
              <Button type="button" variant="secondary" size="sm" onClick={() => handleAddStyle(newStyle)} disabled={!newStyle.trim()}>
                <Plus className="w-3.5 h-3.5" />
              </Button>
            </div>
            {styles.filter(s => !musicStyles.includes(s)).length > 0 && (
              <div className="mb-2 flex flex-wrap gap-1.5">
                {styles.filter(s => !musicStyles.includes(s)).map((style) => (
                  <span key={style} className="inline-flex items-center gap-1.5 px-2.5 py-0.5 bg-primary-900/30 text-primary-400 border border-primary-700/50 rounded-full text-xs font-medium">
                    {style}
                    <button type="button" onClick={() => handleRemoveStyle(style)}><X className="w-3 h-3" /></button>
                  </span>
                ))}
              </div>
            )}
            <div className="flex flex-wrap gap-1.5">
              {musicStyles.map((style) => (
                <button
                  key={style}
                  type="button"
                  onClick={() => styles.includes(style) ? handleRemoveStyle(style) : handleAddStyle(style)}
                  className={clsx(
                    'px-2.5 py-1 rounded-full text-xs font-medium',
                    styles.includes(style)
                      ? 'bg-primary-900/30 text-primary-400 border border-primary-700/50'
                      : 'bg-dark-elevated text-dark-text-secondary border border-dark-border hover:bg-dark-border'
                  )}
                >
                  {styles.includes(style) ? '✓ ' : '+ '}{style}
                </button>
              ))}
            </div>
          </div>
        );

      case 'address':
        return (
          <div>
            <label className="block text-xs font-medium text-dark-text-secondary mb-2">Address</label>
            <div className="grid grid-cols-2 gap-2">
              <Input label="Street" placeholder="123 Main St" value={address.street || ''} onChange={(e) => updateAddressField('street', e.target.value)} />
              <Input label="City" placeholder="Paris" value={address.city || ''} onChange={(e) => updateAddressField('city', e.target.value)} />
              <Input label="Postal Code" placeholder="75001" value={address.zip_code || ''} onChange={(e) => updateAddressField('zip_code', e.target.value)} />
              <Input label="Dept/County" placeholder="Seine" value={address.administrative_region_1 || ''} onChange={(e) => updateAddressField('administrative_region_1', e.target.value)} />
              <Input label="Region/State" placeholder="Ile-de-France" value={address.administrative_region_2 || ''} onChange={(e) => updateAddressField('administrative_region_2', e.target.value)} />
              <Input label="Country" placeholder="France" value={address.country || ''} onChange={(e) => updateAddressField('country', e.target.value)} />
            </div>
          </div>
        );

      case 'concert_period':
        return (
          <div>
            <label className="block text-xs font-medium text-dark-text-secondary mb-2">Concert Period</label>
            {renderMonthPeriodEditor(concertPeriod, setConcertPeriod)}
          </div>
        );

      default:
        return null;
    }
  };

  return createPortal(
    <div
      ref={panelRef}
      className="fixed z-50 bg-dark-elevated border border-dark-border rounded-lg shadow-xl p-4 min-w-[280px] max-w-[480px]"
      style={{ left: adjustedPos.x, top: adjustedPos.y }}
      onContextMenu={(e) => e.preventDefault()}
    >
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-dark-text-primary">Edit {fieldLabel}</h3>
        <button onClick={onClose} className="p-0.5 hover:bg-dark-border rounded-sm text-dark-text-muted hover:text-dark-text-primary">
          <X className="w-4 h-4" />
        </button>
      </div>

      {error && (
        <div className="mb-3 p-2 bg-red-900/20 border border-red-700/50 rounded-sm text-red-400 text-xs">{error}</div>
      )}

      {renderFieldEditor()}

      <div className="mt-3 flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onClose} disabled={isSaving}>Cancel</Button>
        <Button variant="primary" size="sm" onClick={handleSave} isLoading={isSaving}>Update</Button>
      </div>
    </div>,
    document.body
  );
}
